import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError, type ApiListMeta } from '../../lib/api.js';
import type { Concept, ResearchItem } from '../../lib/types.js';
import { StatusBadge } from '../../components/StatusBadge.js';

export function ConceptsListPage() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const concepts = useQuery({
    queryKey: ['concepts'],
    queryFn: () =>
      api.get<Concept[]>('/concepts') as Promise<{ data: Concept[]; meta: ApiListMeta }>,
  });

  const reviewed = useQuery({
    queryKey: ['research', { status: 'reviewed' }],
    queryFn: () =>
      api.get<ResearchItem[]>('/research?status=reviewed&pageSize=100') as Promise<{
        data: ResearchItem[];
      }>,
  });

  const brainstorm = useMutation({
    mutationFn: () => api.post<Concept[]>('/concepts/brainstorm', { researchItemIds: selected }),
    onSuccess: async () => {
      setSelected([]);
      await queryClient.invalidateQueries({ queryKey: ['concepts'] });
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Brainstorm failed'),
  });

  function toggle(id: string) {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  return (
    <section>
      <div className="page-head">
        <h1>Concepts</h1>
        <Link className="button" to="/concepts/new">
          New concept
        </Link>
      </div>

      <div className="review-panel">
        <h2>Brainstorm from reviewed research</h2>
        {reviewed.data && reviewed.data.data.length === 0 && (
          <p className="muted">Review some research first to brainstorm concepts.</p>
        )}
        <ul className="checklist">
          {reviewed.data?.data.map((r) => (
            <li key={r.id}>
              <label>
                <input
                  type="checkbox"
                  checked={selected.includes(r.id)}
                  onChange={() => toggle(r.id)}
                />
                {r.title}
              </label>
            </li>
          ))}
        </ul>
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
        <button
          type="button"
          disabled={selected.length === 0 || brainstorm.isPending}
          onClick={() => {
            setError(null);
            brainstorm.mutate();
          }}
        >
          {brainstorm.isPending ? 'Generating…' : `Brainstorm ${selected.length} selected`}
        </button>
      </div>

      {concepts.isLoading && <p>Loading…</p>}
      {concepts.data && concepts.data.data.length === 0 && (
        <p className="muted">No concepts yet.</p>
      )}
      {concepts.data && concepts.data.data.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Status</th>
              <th>IP risk</th>
              <th>Opportunity</th>
            </tr>
          </thead>
          <tbody>
            {concepts.data.data.map((c) => (
              <tr key={c.id}>
                <td>
                  <Link to={`/concepts/${c.id}`}>{c.title}</Link>
                </td>
                <td>
                  <StatusBadge status={c.status} />
                </td>
                <td>
                  <StatusBadge status={c.ipRisk} />
                </td>
                <td>{Math.round(c.opportunityScore.total * 100)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
